/**
 * notificationStore — minimal in-memory notification feed scaffold.
 *
 * Today this is purely client-side: features push notifications
 * (`pushNotification(...)`) when meaningful background work completes
 * and the user might have moved focus elsewhere. The store seeds the
 * NotificationDrawer; nothing is persisted.
 *
 * Forward-compat: when a backend `/notifications` feed lands, swap
 * `notifications` for a React Query hook that hydrates the same shape.
 * The drawer component reads via this store interface so the swap is
 * a one-line change.
 *
 * Limited to 100 entries — old ones drop off the back so a long-running
 * session doesn't accumulate stale records. Mark-as-read flips
 * `seen=true`; a "clear all" gesture nukes everything past the cap.
 */
import { create } from "zustand";

export type NotificationSeverity = "info" | "success" | "warning" | "error";

export interface Notification {
  id: string;
  /** ISO-8601 timestamp at creation. */
  ts: string;
  severity: NotificationSeverity;
  title: string;
  body?: string;
  /** Optional one-click action — surfaces as a button on the row. */
  action?: { label: string; onClick: () => void };
  seen: boolean;
}

interface NotificationState {
  notifications: Notification[];
  /** Drawer open / closed. */
  drawerOpen: boolean;
}
interface NotificationActions {
  push: (n: Omit<Notification, "id" | "ts" | "seen">) => void;
  markAllSeen: () => void;
  markSeen: (id: string) => void;
  clearAll: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
}

const MAX = 100;

export const useNotifications = create<
  NotificationState & NotificationActions
>()((set) => ({
  notifications: [],
  drawerOpen: false,
  push: (n) =>
    set((s) => {
      const next: Notification = {
        ...n,
        id: cryptoRandomId(),
        ts: new Date().toISOString(),
        seen: false,
      };
      const list = [next, ...s.notifications];
      if (list.length > MAX) list.length = MAX;
      return { notifications: list };
    }),
  markAllSeen: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, seen: true })),
    })),
  markSeen: (id) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, seen: true } : n,
      ),
    })),
  clearAll: () => set({ notifications: [] }),
  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false }),
  toggleDrawer: () => set((s) => ({ drawerOpen: !s.drawerOpen })),
}));

/** Convenience selector — count of unseen notifications, for the badge
 *  on the rack icon. */
export function useUnseenCount(): number {
  return useNotifications((s) => s.notifications.filter((n) => !n.seen).length);
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Cheap fallback for non-secure contexts (dev / SSR).
  return Math.random().toString(36).slice(2, 12);
}
